import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';
import React, {useRef} from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrBarStripe, {
  GraphDataItem,
} from '../../../common/arr-bar-stripe/ArrBarStripe';
import './ArrIngestionTile.scss';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import {useTranslation} from 'react-i18next';

interface DocumentTileProps {
  primaryLabel?: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
  primaryLabelColor?: string;
  secondaryLabelColor?: string;
  tertiaryLabelColor?: string;
  status?: string;
  lastModifiedBy?: string;
  lastModifiedTimestamp?: string;
  statusTimestamp?: string;
  metaDataList?: ITileMetadataItem[];
  bates?: IBates;
  actionMenuItems?: CustomMenuItem[];
  progressBarProps: IProgressBarProps;
  onPrimaryLabelClick?: () => void;
  dataTestId: string;
}

export interface ITileMetadataItem {
  label: string;
  value: string | number | boolean;
}

export interface IBates {
  startNumber?: string;
  endNumber?: string;
}

export interface IProgressBarProps {
  total?: string;
  graphData: Array<GraphDataItem>;
  stripeWidth?: number;
  gap?: number;
  height?: number;
  percent?: number;
  state?:
    | 'in-progress'
    | 'success'
    | 'failed'
    | 'draft'
    | 'completed'
    | 'approved'
    | 'published'
    | 'indexed'
    | 'copied'
    | 'catalog-complete';
}

const DocumentTile: React.FC<DocumentTileProps> = ({
  primaryLabel,
  secondaryLabel,
  tertiaryLabel,
  primaryLabelColor,
  secondaryLabelColor,
  tertiaryLabelColor,
  status,
  lastModifiedBy,
  lastModifiedTimestamp,
  statusTimestamp,
  metaDataList,
  bates,
  actionMenuItems = [],
  progressBarProps,
  dataTestId,
  onPrimaryLabelClick,
}) => {
  const {t} = useTranslation();
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const onButtonClick1 = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };
  const onItemClick = (item: CustomMenuItem) => {
    item.onClick?.();
  };
  return (
    <Card
      component="article"
      elevation={0}
      className="ingestion-arr-feature-card"
    >
      <CardHeader
        className="arr-feature-card__header"
        disableTypography
        title={
          <>
            {secondaryLabelColor && (
              <Typography
                className={`arr-feature-card__label--secondary typo-text label3_size text-ellipsis ${secondaryLabelColor || 'text-headings'}`}
                data-testid={`arr-ingestion-tile-sec-label-${dataTestId}`}
              >
                {secondaryLabel}
              </Typography>
            )}
            {primaryLabel && (
              <Typography
                onClick={onPrimaryLabelClick || undefined}
                component="h3"
                variant="body1"
                style={{cursor: onPrimaryLabelClick ? 'pointer' : 'default'}}
                className={`arr-feature-card__label--primary typo-text subhead3_size text-ellipsis ${primaryLabelColor || 'text-headings'}`}
                data-testid={`arr-ingestion-primary-label-${dataTestId}`}
              >
                {primaryLabel}
              </Typography>
            )}
            {tertiaryLabel && (
              <Typography
                role="output"
                aria-label={`Status: ${tertiaryLabel}`}
                className={`arr-feature-card__label--tertiary typo-text subhead4_size text-ellipsis ${tertiaryLabelColor || 'text-headings'}`}
                data-testid={`arr-ingestion-tile-typography-tertiary-label-${dataTestId}`}
              >
                {tertiaryLabel}
              </Typography>
            )}
          </>
        }
        action={
          actionMenuItems.length ? (
            <>
              <ArrMenu
                id={`arr-menu-1-${dataTestId}}`}
                ref={menuRef1}
                items={actionMenuItems}
                onItemClick={onItemClick}
                direction="bottom-right"
                className="action-dropbox"
              />
              <ArrIconButton
                onClick={onButtonClick1}
                size="medium"
                shape="default"
                icon={<MoreVertIcon />}
                ariaLabel="menu-icon"
                className="no-bg-icon"
                aria-label={t('ingestions.mapped.infoIcon')}
                testId={`arr-ingestion-tile-menu-icon-icon-button-${dataTestId}`}
              />
            </>
          ) : null
        }
      ></CardHeader>
      <CardContent>
        <Typography
          role="output"
          aria-label={`Status: ${status}`}
          className="arr-feature-card__label--progress-status typo-text label3_size"
          data-testid={`arr-ingestion-tile-status-${dataTestId}`}
        >
          {status}
        </Typography>
        <Box>
          {(() => {
            const normalizeState = (
              raw?: string
            ):
              | 'in-progress'
              | 'success'
              | 'failed'
              | 'draft'
              | 'approved'
              | 'published'
              | 'indexed'
              | 'copied'
              | 'catalog-complete' => {
              const s = (raw || '').toLowerCase().replaceAll(/[\s_]/g, '');
              if (s.includes('progress')) return 'in-progress';
              if (s.includes('approved')) return 'approved';
              if (s.includes('published')) return 'published';
              if (s.includes('fail')) return 'failed';
              if (s.includes('indexed')) return 'indexed';
              if (s.includes('copied')) return 'copied';
              if (s.includes('cataloged')) return 'catalog-complete';
              if (s.includes('success')) return 'success';
              return 'draft';
            };

            const derivedState = normalizeState(
              progressBarProps?.state || status
            );
            const isPercentMode = typeof progressBarProps?.percent === 'number';
            return (
              <ArrBarStripe
                height={progressBarProps?.height}
                state={derivedState}
                mode={isPercentMode ? 'percent' : 'graph'}
                total={progressBarProps.total ?? '100'}
                percent={progressBarProps?.percent}
                stripeWidth={
                  isPercentMode ? 2 : progressBarProps?.stripeWidth || 2
                }
                gap={isPercentMode ? 0.8 : progressBarProps?.gap || 2}
                dataTestId={`arr-ingestion-tile-progress-bar-${dataTestId}`}
              />
            );
          })()}
        </Box>

        <Box className="arr-feature-card__content">
          <>
            {metaDataList?.map((data, index) => (
              <Box
                className="arr-feature-card__meta-data-box"
                key={data.label}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Typography
                  className="typo-text label3_size text-body-subtle"
                  data-testid={`arr-ingestion-tile-typography-label-${dataTestId}-${index}`}
                >
                  {data.label}
                </Typography>
                <Typography
                  className="typo-text subhead2_size text-headings"
                  data-testid={`arr-ingestion-tile-typography-value-${dataTestId}-${index}`}
                >
                  {data.value}
                </Typography>
              </Box>
            ))}
            {bates && (
              <Box className="arr-feature-card__bates">
                <Typography
                  className="typo-text label3_size text-body-subtle"
                  data-testid={`arr-ingestion-tile-title-${dataTestId}`}
                >
                  {t('common.documentTile.bates.title')}
                </Typography>
                <Box className="arr-feature-card__bates-box">
                  <Typography
                    className="typo-text buttonXS_size text-body-subtle"
                    data-testid={`arr-ingestion-tile-start-${dataTestId}`}
                  >
                    {t('common.documentTile.bates.start')}
                  </Typography>
                  <Typography
                    className="typo-text buttonXS_size text-headings text-ellipsis-start"
                    data-testid={`arr-ingestion-tile-start-number-${dataTestId}`}
                  >
                    {bates.startNumber || '\u00A0'}
                  </Typography>
                </Box>
                <Box className="arr-feature-card__bates-box">
                  <Typography
                    className="typo-text buttonXS_size text-body-subtle"
                    data-testid={`arr-ingestion-tile-end-${dataTestId}`}
                  >
                    {t('common.documentTile.bates.end')}
                  </Typography>
                  <Typography
                    className="typo-text buttonXS_size text-headings text-ellipsis-start"
                    data-testid={`arr-ingestion-tile-typography-end-number-${dataTestId}`}
                  >
                    {bates.endNumber || '\u00A0'}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        </Box>
        <Divider></Divider>
        <Box className="arr-feature-card__last-modified">
          <Typography
            className="typo-text label3_size text-body-subtle"
            data-testid={`arr-ingestion-tile-last-modified-${dataTestId}`}
          >
            {t('common.documentTile.lastModified')}
          </Typography>
          <Typography
            className="typo-text body4_size text-headings text-ellipsis"
            data-testid={`arr-ingestion-tile-typography-timestamp-${dataTestId}`}
          >
            {lastModifiedBy + ' ' + lastModifiedTimestamp}
          </Typography>
        </Box>
        <Box>
          <Typography
            className="typo-text label3_size text-body-subtle"
            data-testid={`arr-ingestion-tile-last-status-${dataTestId}`}
          >
            {t('common.documentTile.lastStatus')}
          </Typography>
          <Typography
            className="typo-text body4_size text-headings text-ellipsis"
            data-testid={`arr-ingestion-tile-last-completed-status-${dataTestId}`}
          >
            {t('common.documentTile.lastCompletedStatus')}
            {statusTimestamp}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentTile;
